import React, { FC, useMemo, useState } from 'react';
import { BoxV2 as Box, Flex, useHasPermission } from 'portal-commons';
import { CspProfile } from '@/types/user';
import {
  faDollar,
  faGearCode,
  faIndustryWindows,
  faMessageDots,
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { emailArrayToString } from '../../CspRegistration/helpers';
import { profileController } from '@/apis/core';
import { EmailField as EmailFieldType } from '../../CspRegistration/types';
import { toastFlashMessage } from '@/utils';
import { splitEmail } from '../utils';
import SectionCard from './SectionCard';
import EmailEditModal, { EmailEditPayload } from './EmailEditModal';

interface TabEmailPreferencesProps {
  profile: CspProfile;
  onUpdate: (updated: CspProfile) => void;
}

enum EmailType {
  CommercialContacts = 'commercial',
  SystemNotifications = 'system',
  FinancialContacts = 'financial',
  TechnicalContacts = 'technical',
}

const EMAIL_SETTINGS: Record<
  EmailType,
  {
    title: string;
    description: string;
    payloadKey: 'email' | 'escalateEmail' | 'financeEmail' | 'techEmail';
    icon: IconDefinition;
  }
> = {
  [EmailType.CommercialContacts]: {
    title: 'Commercial Contact',
    description: 'Used for contract and business communications.',
    payloadKey: 'email',
    icon: faIndustryWindows,
  },
  [EmailType.SystemNotifications]: {
    title: 'System Notification',
    description: 'Used for system event notifications.',
    payloadKey: 'escalateEmail',
    icon: faMessageDots,
  },
  [EmailType.FinancialContacts]: {
    title: 'Financial Contact',
    description: 'Used for invoicing and finance communications.',
    payloadKey: 'financeEmail',
    icon: faDollar,
  },
  [EmailType.TechnicalContacts]: {
    title: 'Technical Contact',
    description:
      'Used to communicate with your support team. Also serves as a fallback if other provided addresses do not respond to contact.',
    payloadKey: 'techEmail',
    icon: faGearCode,
  },
};

const EmailGrid: FC = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      rowGap: '12px',
      mt: '12px',
    }}
  >
    {children}
  </Box>
);

const EmailItem: FC<{ value: string }> = ({ value }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '170px 1fr' }}>
    <Box
      sx={{
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#68737A',
      }}
    >
      Email Address:
    </Box>
    <Box
      sx={{
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#68737A',
        wordBreak: 'break-all',
      }}
    >
      {value || 'N/A'}
    </Box>
  </Box>
);

const TabEmailPreferences: FC<TabEmailPreferencesProps> = ({
  profile,
  onUpdate,
}) => {
  const canEditEmail = useHasPermission('profile.editEmail') as boolean;
  const [modal, setModal] = useState<EmailType | null>(null);
  const [updating, setUpdating] = useState(false);

  const commercialEmails = useMemo(
    () => splitEmail(profile.email),
    [profile.email]
  );
  const systemEmails = useMemo(
    () => splitEmail(profile.escalateEmail),
    [profile.escalateEmail]
  );
  const financialEmails = useMemo(
    () => splitEmail(profile.financeEmail),
    [profile.financeEmail]
  );
  const technicalEmails = useMemo(
    () => splitEmail(profile.techEmail),
    [profile.techEmail]
  );

  const handleUpdateEmail = async (data: EmailEditPayload) => {
    if (!modal) return;

    setUpdating(true);
    const updatedEmailString = emailArrayToString(
      data.emails as EmailFieldType[]
    );

    const payload = {
      [EMAIL_SETTINGS[modal].payloadKey]: updatedEmailString,
    };

    try {
      const response = await profileController.updateProfileEmail(payload);
      if (response) {
        toastFlashMessage('Email Preferences Updated', 'success');
        onUpdate(response);
        setModal(null);
      }
    } finally {
      setUpdating(false);
    }
  };

  const getEmailsByType = (type: EmailType) => {
    switch (type) {
      case EmailType.CommercialContacts:
        return commercialEmails;
      case EmailType.SystemNotifications:
        return systemEmails;
      case EmailType.FinancialContacts:
        return financialEmails;
      case EmailType.TechnicalContacts:
        return technicalEmails;
      default:
        return [];
    }
  };

  return (
    <Flex
      sx={{ flexDirection: 'column', rowGap: '32px', mt: '7px' }}
      data-testid="tabEmailPreferencesContainer"
    >
      {Object.entries(EMAIL_SETTINGS).map(([key, settings]) => {
        const type = key as EmailType;
        const sectionEmails = getEmailsByType(type);

        return (
          <SectionCard
            key={type}
            icon={settings.icon}
            title={settings.title}
            description={settings.description}
            editable={canEditEmail}
            onEdit={() => {
              canEditEmail && setModal(type);
            }}
            dataTestId={`${type}Section`}
            sx={{
              '& button': {
                fontWeight: 400,
                '& svg': { fontSize: '12px' },
                '& span': { p: 0 },
              },
            }}
          >
            <EmailGrid>
              {sectionEmails.length > 0 ? (
                sectionEmails.map((email, index) => (
                  <EmailItem key={index} value={email} />
                ))
              ) : (
                <EmailItem value="" />
              )}
            </EmailGrid>
          </SectionCard>
        );
      })}

      {modal && (
        <EmailEditModal
          open={!!modal}
          disabled={updating}
          title={EMAIL_SETTINGS[modal].title}
          icon={EMAIL_SETTINGS[modal].icon}
          description={EMAIL_SETTINGS[modal].description}
          emails={getEmailsByType(modal)}
          onClose={() => setModal(null)}
          onSubmit={handleUpdateEmail}
        />
      )}
    </Flex>
  );
};

export default TabEmailPreferences;
